import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { initialize, change } from 'redux-form';
import { getCountryCallingCode } from 'react-phone-number-input';
import { useTranslation, Trans } from 'react-i18next';
import { Spacing, Text, Icon } from '@reservamos/elements';
import InsuranceSection from 'components/purchase/InsuranceSection';
import SeatSelectorFactory from 'components/seats/SeatSelectorFactory';
import BookingReview from 'ui/atoms/BookingReview';
import AncillariesSwitch from 'ui/atoms/AncillariesSwitch';
import LoadingScreen from 'components/LoadingScreen';
import parseQueryString from 'utils/queryString/parseQueryString';
import { getStoredPurchaser } from 'utils/storedPurchaser';
import { getBrand2ProcessorMap } from 'utils/documentMaps';
import {
  passengers as validatePassengers,
  documentId as validateDocumentId,
} from 'utils/formValidations';
import { useInsuranceEligibility } from 'hooks/useInsuranceEligibility';
import useExperimentalFeature from 'hooks/useExperimentalFeature';
import {
  getQuickPurchaseInsuranceAutoDisableKey,
  getQuickPurchaseInsuranceAvailability,
  hasSelectedPassengerInsurance,
  shouldAutoDisableQuickPurchaseInsurance,
} from 'utils/quickPurchaseInsurance';
import quickPurchaseIconSrc from 'images/purchases/quick-purchase.png';
import {
  quickPurchaseBackToSearchTracker,
  quickPurchaseIncompleteDataTracker,
  quickPurchasePassengerEditTracker,
  quickPurchaseSeatsEditTracker,
  quickPurchaseViewTracker,
} from 'metrics/user-analytics/purchase';
import errorMonitoring from 'errorMonitoring/ErrorMonitoring';
import wayIsOpenTicket, { isHybridTrip } from '../../../utils/wayIsOpenTicket';
import QuickCheckoutTravelers from './QuickCheckoutTravelers';
import QuickCheckoutPassengersForm, {
  createPassengerSaveOnSubmit,
} from './QuickCheckoutPassengersForm';
import QuickPurchaseTimerBadge from './QuickPurchaseTimerBadge';
import useQuickCheckoutPassengersInitialValues from '../../../hooks/purchase/useQuickCheckoutPassengersInitialValues';
import useQuickCheckoutState from '../../../hooks/purchase/useQuickCheckoutState';
import useQuickCheckoutActions from '../../../hooks/purchase/useQuickCheckoutActions';
import useBuyerManuallyEdited from '../../../hooks/purchase/useBuyerManuallyEdited';
import PaymentPanel from '../PaymentPanel';
import NotWsSandboxWarning from '../NotWsSandboxWarning';
import useLoyaltyPrograms from '../../../loyalty/context/useLoyaltyPrograms';

/**
 * Returns true when a form value is missing (null, empty, or whitespace-only).
 * @param {string} value - The value to check.
 * @returns {boolean} Whether the value is blank.
 */
const isBlankValue = (value) => !value || String(value).trim() === '';

/**
 * QuickCheckout component.
 * Render a streamlined checkout screen for Quick Purchase. On mount, completes the
 * purchase orchestration (fetch details, auto-select seats, save passengers) while
 * showing a loading screen. Once ready, displays read-only trip summary, seats,
 * passengers, and payment form.
 * @param {object} props - history, location, goToSearch, orchestrationParams, isModal, onClose.
 * @param {object} props.history - React Router history object.
 * @param {object} props.location - React Router location object.
 * @param {Function} props.goToSearch - Navigate back to search/schedules page.
 * @param {object} [props.orchestrationParams] - Optional orchestration parameters (passengersParam,
 * selectedSeatNumbers).
 * @param {Function} props.onOrchestrationChange - Sync orchestration loading state with Purchase sidebar.
 * @param {boolean} [props.isModal] - Render QuickCheckout in modal mode.
 * @param {Function} [props.onClose] - Callback to close modal mode.
 * @param {number} [props.timerRemainingTime] - Remaining purchase time in seconds.
 */
const QuickCheckout = ({
  history,
  location,
  goToSearch,
  orchestrationParams,
  onOrchestrationChange,
  isModal,
  onClose,
  timerRemainingTime,
}) => {
  const { t } = useTranslation(['purchase', 'insurance', 'payment']);
  const [isOrchestrating, setIsOrchestrating] = useState(true);
  const [orchestrationError, setOrchestrationError] = useState(null);
  const [notWsSandboxWarningDismissed, setNotWsSandboxWarningDismissed] = useState(false);
  // Lifted up from usePaymentSection so updating this state triggers a QuickCheckout
  // re-render that re-reads the latest store state (isUpdating, updatingInsurance, ...).
  // Without this, PaymentPanel re-renders alone with stale props after the purchaser
  // save and the pending-payment effect resumes payment before the latest state lands.
  const [purchaserSubmitted, setPurchaserSubmitted] = useState(false);
  const handlePurchaserSubmitted = useCallback(() => setPurchaserSubmitted(true), []);
  const [isEditingPassengers, setIsEditingPassengers] = useState(false);
  // When frequent/stored passenger or buyer data is missing tenant-required fields (e.g. an empty
  // document id), we default the affected form(s) to open so the user is forced through validation
  // before paying (CON-1076). Computed once after orchestration; a later manual toggle wins.
  // null means the one-shot incomplete-data evaluation has not finished yet. Keep the payment
  // section unmounted until this becomes a boolean so PurchaserForm reads the correct initial value.
  const [buyerFormDefaultExpanded, setBuyerFormDefaultExpanded] = useState(null);
  const forcedIncompleteFormsRef = useRef(false);
  // Inline seat editing: swaps the QuickCheckout body for the seat selector without navigating.
  const [isEditingSeats, setIsEditingSeats] = useState(false);
  // Snapshot of the seats held when entering edit, to restore them if the user cancels without
  // re-locking (the seat selector mutates purchase.departs.selectedSeats on every tap).
  const originalSeatsRef = useRef(null);
  // Snapshot of the passenger/purchaser redux-form values captured when entering the inline seat
  // editor. Those edits live only in redux-form (not persisted until "Finalizar compra"), so both
  // the summary unmount (destroyOnUnmount) and the seat relock's getPurchase (which mergeDeeps the
  // pre-edit backend purchase) would wipe them. Restored on returning from the editor (PRO2-8).
  const preservedFormsRef = useRef(null);
  const passengersOnSuccessRef = useRef(null);
  const autoDisableInsuranceAttemptRef = useRef(null);
  const dispatch = useDispatch();
  const { userIsLoggedInWithAnyLoyaltyProgram } = useLoyaltyPrograms();
  const quickPurchaseLoyaltyExitRef = useRef(false);
  // Controlled rollout: when off, the edit-seats button redirects to the seats page
  // (previous behavior); when on, it swaps the seat selector in place.
  const seatEditInPlaceEnabled = useExperimentalFeature('QUICK_PURCHASE_SEAT_EDIT_ENABLED');

  const {
    purchase,
    payment,
    env,
    features,
    isUpdating,
    fetchingPayment,
    paymentId,
    cardErrorOccured,
    cardValues,
    paymentEngine,
    selectedPaymentMethod,
    purchaser,
    purchaserFormValues,
    insuranceMessage,
    insuranceAmount,
    insuranceAmountWithTaxes,
    insuranceAmountIsUnitPrice,
    carbonOffset,
    wantsCarbonOffset,
    updatingCarbonOffset,
  } = useQuickCheckoutState();

  const actions = useQuickCheckoutActions({ history });
  const passengersInitialValues = useQuickCheckoutPassengersInitialValues();
  const buyerManuallyEdited = useBuyerManuallyEdited();

  const {
    token,
    departs,
    returns,
    passengers,
    total,
    qualifiesForInsurance,
    wantsInsurance,
    updatingInsurance,
  } = purchase;

  const routeQuery = parseQueryString(location?.search || '');
  const searchPassengers = orchestrationParams?.passengersParam || routeQuery.passengers || 'A1';
  const selectedSeatNumbers =
    orchestrationParams?.selectedSeatNumbers ||
    (routeQuery.seats ? routeQuery.seats.split(',') : null);
  const insuranceEligibility = useInsuranceEligibility({ purchaser, purchase });

  useEffect(() => {
    onOrchestrationChange?.(isOrchestrating);

    return () => {
      onOrchestrationChange?.(false);
    };
  }, [isOrchestrating, onOrchestrationChange]);

  useEffect(() => {
    if (!actions.onCompleteQuickPurchase) return;

    // Skip orchestration if purchase is already set up (e.g., page reload)
    if (passengers?.length > 0 && purchase.hasTickets) {
      setIsOrchestrating(false);
      quickPurchaseViewTracker();
      return;
    }

    actions
      .onCompleteQuickPurchase({
        passengers: searchPassengers,
        selectedSeatNumbers,
      })
      .then((result) => {
        setIsOrchestrating(false);

        if (result.success) {
          quickPurchaseViewTracker();
        }

        if (!result.success) {
          errorMonitoring.notify({
            error: new Error(`Quick checkout orchestration failed: ${result.error}`),
            info: { from: 'QuickCheckout:orchestration', error: result.error, token },
          });
          setOrchestrationError(result.error);

          if (
            result.error === 'NOT_ENOUGH_SEATS_DEPARTURE' ||
            result.error === 'NOT_ENOUGH_SEATS_RETURN'
          ) {
            actions.transitionTo(`/purchase/${result.token || token}/seats/departure`);
          }
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch Yuno session data after orchestration completes
  useEffect(() => {
    if (isOrchestrating || orchestrationError) return;
    if (!env.yuno?.enabled || !total || total <= 0) return;

    const { api: { purchaseUrl } = {} } = env;
    actions.onFetchYunoData({ purchaseUrl, purchaseToken: token, amount: total });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrchestrating]);

  // Pre-fill purchaser form: first passenger data takes priority, localStorage as fallback
  useEffect(() => {
    if (isOrchestrating || orchestrationError) return;

    const firstPassenger = passengers?.[0] || {};
    const storedPurchaser = getStoredPurchaser();
    const phoneCountry = storedPurchaser?.phoneCountry || env.lang?.default?.substring(3) || 'MX';
    let phoneCode = '';
    try {
      phoneCode = getCountryCallingCode(phoneCountry);
    } catch (e) {
      // Invalid country code, leave empty
    }

    // After orchestration the backend returns document_type as the processor value (e.g. "PP").
    // We need to reverse-map it back to the brand value (e.g. "PAS") so the purchaser form
    // initializes with a value that matches the DocumentTypeField dropdown options.
    const brand2ProcessorMap = getBrand2ProcessorMap();
    const processor2BrandMap = Object.fromEntries(
      Object.entries(brand2ProcessorMap).map(([brand, proc]) => [proc, brand]),
    );
    const passengerDocType = firstPassenger.documentType;
    const brandDocType =
      processor2BrandMap[passengerDocType] ||
      passengerDocType ||
      storedPurchaser?.documentType ||
      'CC';

    const purchaserInitialValues = {
      purchaserFirstName: firstPassenger.firstName || storedPurchaser?.purchaserFirstName || '',
      purchaserLastName: firstPassenger.lastName || storedPurchaser?.purchaserLastName || '',
      purchaserSecondLastName:
        firstPassenger.secondLastName || storedPurchaser?.purchaserSecondLastName || '',
      email: firstPassenger.email || storedPurchaser?.email || '',
      phone: storedPurchaser?.phone || '',
      phoneCountry,
      phoneCode,
      documentType: brandDocType,
      documentId: firstPassenger.documentId || storedPurchaser?.documentId || '',
      promotionalTerms: true,
    };

    dispatch(initialize('purchaser', purchaserInitialValues));

    // Default the buyer/passenger forms to open when their tenant-required data is incomplete, so
    // the existing validators run and block payment before an empty document reaches the backend
    // (CON-1076). One-shot: never override a subsequent manual toggle.
    if (!forcedIncompleteFormsRef.current) {
      forcedIncompleteFormsRef.current = true;

      // Passengers: reuse the exact form-level validator the mounted passengers form uses. It reads
      // the tenant flags from the store, so it enforces only the fields this client requires. Its
      // return is an errors array (one entry per passenger) — collect each missing field key.
      // `email` is special: the shared validator hard-requires it for passenger 0 regardless of
      // tenant, but only clients that actually collect a passenger email (PASSENGERS_EXTRA_FIELDS /
      // SHOW_PASSENGERS_EMAIL_FIELD, matching PersonPassengersForm) should let a missing email force
      // this form open — otherwise it fires for tenants that don't ask for it (CON-1076 feedback).
      const collectsPassengerEmail = Boolean(
        features.PASSENGERS_EXTRA_FIELDS || features.SHOW_PASSENGERS_EMAIL_FIELD,
      );
      const passengerErrors = validatePassengers(passengers);
      const passengerMissingFields = [];
      if (Array.isArray(passengerErrors)) {
        passengerErrors.forEach((error, index) => {
          if (error && typeof error === 'object') {
            Object.keys(error).forEach((field) => {
              if (field === 'email' && !collectsPassengerEmail) return;
              if (error[field]) passengerMissingFields.push(`passenger_${index + 1}.${field}`);
            });
          }
        });
      }
      if (passengerMissingFields.length > 0) setIsEditingPassengers(true);

      // Buyer: only the identity/document fields are hidden while the purchaser form is collapsed
      // (email/phone stay registered and validated), so those are the only fields that can slip
      // through. Force-expand when any of them is missing/invalid.
      const usesBusinessName =
        Boolean(features.ACCEPT_PURCHASER_BUSINESS_NAME) &&
        purchaserInitialValues.documentType === 'NIT';
      const buyerMissingFields = [];
      if (isBlankValue(purchaserInitialValues.purchaserFirstName)) {
        buyerMissingFields.push('buyer.purchaserFirstName');
      }
      if (!usesBusinessName && isBlankValue(purchaserInitialValues.purchaserLastName)) {
        buyerMissingFields.push('buyer.purchaserLastName');
      }
      if (features.IDENTIFICATION_DOCUMENT_FOR_PURCHASER) {
        if (isBlankValue(purchaserInitialValues.documentType)) {
          buyerMissingFields.push('buyer.documentType');
        }
        if (
          validateDocumentId(purchaserInitialValues.documentId, {
            documentType: purchaserInitialValues.documentType,
          })
        ) {
          buyerMissingFields.push('buyer.documentId');
        }
      }
      setBuyerFormDefaultExpanded(buyerMissingFields.length > 0);

      // Emit a single analytics event listing every missing field so we can size and diagnose the
      // incomplete-frequent-passenger data issue this fix guards against (CON-1076).
      const missingFields = [...passengerMissingFields, ...buyerMissingFields];
      if (missingFields.length > 0) {
        quickPurchaseIncompleteDataTracker(missingFields);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrchestrating]);

  // Redirect to PaymentProcessor after payment confirmation completes
  const prevFetchingPayment = useRef(false);
  useEffect(() => {
    if (prevFetchingPayment.current && !fetchingPayment && paymentId) {
      actions.transitionTo(`/payment/${token}/${paymentId}`);
    }
    prevFetchingPayment.current = fetchingPayment;
  }, [fetchingPayment, paymentId, token, actions]);

  const passengerInsuranceAvailability = useMemo(
    () => getQuickPurchaseInsuranceAvailability({ features, purchase }),
    [features, purchase],
  );
  const usesPassengerInsurance = Boolean(features.PASSENGERS_INSURANCE_ENABLED);
  const isHybrid = isHybridTrip(departs, returns);
  const showMessageNotInsuranceOpenTicket = isHybrid && !features.OPENT_TICKET_INSURANCE_ENABLED;
  const enablesCheckoutInsurance =
    !usesPassengerInsurance && qualifiesForInsurance && !showMessageNotInsuranceOpenTicket;
  const enablesInsurance = usesPassengerInsurance
    ? passengerInsuranceAvailability.enabled
    : enablesCheckoutInsurance;
  const selectedInsurance = usesPassengerInsurance
    ? hasSelectedPassengerInsurance(purchase)
    : Boolean(wantsInsurance);
  const insuranceDisabled = Boolean(insuranceEligibility.hasIneligibleDoc);
  const autoDisableInsuranceAttemptKey = getQuickPurchaseInsuranceAutoDisableKey({
    token,
    usesPassengerInsurance,
    passengerDocs: insuranceEligibility.passengerDocs,
  });

  const handleInsuranceToggle = useCallback(
    (event) => {
      const wants = event.target.checked;

      if (usesPassengerInsurance) {
        actions.onTogglePassengerInsurance(token, wants);
        return;
      }

      actions.onToggleWantsInsurance(token, wants);
    },
    [actions, token, usesPassengerInsurance],
  );

  useEffect(() => {
    if (autoDisableInsuranceAttemptRef.current !== autoDisableInsuranceAttemptKey) {
      autoDisableInsuranceAttemptRef.current = null;
    }

    if (!enablesInsurance || !selectedInsurance || !insuranceDisabled) {
      autoDisableInsuranceAttemptRef.current = null;
    }
  }, [autoDisableInsuranceAttemptKey, enablesInsurance, insuranceDisabled, selectedInsurance]);

  useEffect(() => {
    if (
      !shouldAutoDisableQuickPurchaseInsurance({
        isOrchestrating,
        enablesInsurance,
        selectedInsurance,
        insuranceDisabled,
        updatingInsurance,
        attemptedKey: autoDisableInsuranceAttemptRef.current,
        attemptKey: autoDisableInsuranceAttemptKey,
      })
    ) {
      return;
    }

    autoDisableInsuranceAttemptRef.current = autoDisableInsuranceAttemptKey;

    if (usesPassengerInsurance) {
      actions.onTogglePassengerInsurance(token, false);
      return;
    }

    actions.onToggleWantsInsurance(token, false);
  }, [
    actions,
    enablesInsurance,
    insuranceDisabled,
    isOrchestrating,
    autoDisableInsuranceAttemptKey,
    selectedInsurance,
    token,
    updatingInsurance,
    usesPassengerInsurance,
  ]);

  // Once a payment reference has already been generated (e.g. store/OXXO payments, where
  // the reference is issued before the purchase completes), seats/passengers must not be
  // editable anymore — mirrors the redirect guard in Purchase.js for the non-quick flow.
  // Store references stay blocking regardless of status (failed/canceled included): unlike
  // card/transfer attempts, generating a new one is a backend-side concern that a client-side
  // retry should not trigger, so once one exists for this purchase, editing stays locked.
  const hasBlockingPaymentAttempt = Boolean(
    paymentId &&
      (payment.paymentType === 'store' ||
        (payment.status && !['failed', 'canceled'].includes(payment.status))),
  );

  const handleSeatsEdit = useCallback(
    (way) => {
      if (hasBlockingPaymentAttempt) {
        actions.transitionTo(`/payment/${token}/${paymentId}`);
        return;
      }
      quickPurchaseSeatsEditTracker();

      if (!seatEditInPlaceEnabled) {
        // Previous behavior: navigate to the standard seat selection page.
        actions.onClearSelectedSeats();
        actions.transitionTo(`/purchase/${token}/seats/${way}`);
        return;
      }

      // Snapshot the in-progress purchaser form edits before the summary unmounts, so they can be
      // restored on return (PRO2-8). The passengers form no longer needs identity snapshotting: it
      // keeps dirty edits across the seat-editor unmount and store reverts (destroyOnUnmount:false
      // + keepDirtyOnReinitialize:true). Seats are synced separately via change() on return.
      preservedFormsRef.current = {
        purchaser: purchaserFormValues,
      };

      // Keep a snapshot so cancel can restore the locked tickets, but start the editor empty
      // to match the normal purchase flow when users re-enter seat selection.
      originalSeatsRef.current = departs.selectedSeats || [];
      actions.onSelectSeats('departs', []);
      setIsEditingSeats(true);
    },
    [
      seatEditInPlaceEnabled,
      actions,
      token,
      departs.selectedSeats,
      purchaserFormValues,
      hasBlockingPaymentAttempt,
      paymentId,
    ],
  );

  // Restore the purchaser form edits captured in handleSeatsEdit once the inline seat editor closes
  // (confirm or cancel). Runs after the summary block has remounted, so this initialize wins over
  // the form's own mount-time initialize from the (reverted) store.
  // Also sync passengers[i].seats from store-derived initialValues: seats are not registered Fields,
  // so keepDirtyOnReinitialize does not refresh them (header would stay stale). Only change seats —
  // never initialize('passengers') — so dirty identity edits stay dirty across later getPurchase
  // reverts (PRO2-8).
  useEffect(() => {
    if (isEditingSeats) return;
    const preserved = preservedFormsRef.current;
    if (!preserved) return;
    preservedFormsRef.current = null;
    if (preserved.purchaser) dispatch(initialize('purchaser', preserved.purchaser));
    const freshPassengers = passengersInitialValues?.passengers || [];
    freshPassengers.forEach((passenger, index) => {
      if (passenger?.seats) {
        dispatch(change('passengers', `passengers[${index}].seats`, passenger.seats));
      }
    });
  }, [isEditingSeats, dispatch, passengersInitialValues]);

  const handleSeatsConfirmed = useCallback(() => {
    // Only fire the re-lock. The success/failure transition is driven by the isLockingTickets
    // effect below, not by this call's result: lockTickets swallows errors and resolves
    // regardless, so an awaited result cannot tell success from failure.
    actions.onUpdateQuickPurchaseSeats(token, departs.selectedSeats || []);
  }, [actions, departs.selectedSeats, token]);

  // Drive the inline seat editor's outcome from the re-lock's Redux state instead of the thunk's
  // promise. When isLockingTickets falls back to false after a save, close the editor (back to the
  // summary) only if no ticketsError was recorded; otherwise stay in the editor so the user retries
  // with the refreshed availability (the error toast + bus refresh already happened in the thunk).
  const wasLockingRef = useRef(false);
  useEffect(() => {
    const isLocking = purchase.isLockingTickets;
    if (isEditingSeats && wasLockingRef.current && !isLocking) {
      if (!purchase.ticketsErrorType) {
        originalSeatsRef.current = null;
        setIsEditingSeats(false);
      }
    }
    wasLockingRef.current = isLocking;
  }, [purchase.isLockingTickets, purchase.ticketsErrorType, isEditingSeats]);

  const handleSeatsEditCancel = useCallback(() => {
    if (originalSeatsRef.current) {
      actions.onSelectSeats('departs', originalSeatsRef.current);
      originalSeatsRef.current = null;
    }
    setIsEditingSeats(false);
  }, [actions]);

  const handlePassengersEdit = useCallback(() => {
    if (hasBlockingPaymentAttempt) {
      actions.transitionTo(`/payment/${token}/${paymentId}`);
      return;
    }
    quickPurchasePassengerEditTracker();
    setIsEditingPassengers(true);
  }, [hasBlockingPaymentAttempt, actions, token, paymentId]);

  const handlePassengersSaved = useCallback(() => {
    if (passengersOnSuccessRef.current) {
      passengersOnSuccessRef.current.resolve();
      passengersOnSuccessRef.current = null;
    }
    setIsEditingPassengers(false);
  }, []);

  const handlePassengersSubmitFail = useCallback(() => {
    if (passengersOnSuccessRef.current) {
      passengersOnSuccessRef.current.reject(new Error('passengers_validation_failed'));
      passengersOnSuccessRef.current = null;
    }
    // Surface the "check the fields in red" warning below, mirroring the purchaser form's own
    // submit-fail behavior (PurchaserForm/index.js) and the regular Checkout (CON-1076).
    actions.onFormFail();
  }, [actions]);

  const handleBeforePaymentAsync = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (passengersOnSuccessRef.current) {
        passengersOnSuccessRef.current.reject(new Error('passengers_superseded'));
      }
      passengersOnSuccessRef.current = { resolve, reject };
      actions.onSubmitForm('passengers');
    });
  }, [actions]);

  const handlePassengersSubmit = useCallback(
    (fields) =>
      createPassengerSaveOnSubmit(dispatch, purchase, {
        buyerManuallyEdited,
        purchaserFormValues,
      })(fields),
    [dispatch, purchase, buyerManuallyEdited, purchaserFormValues],
  );

  const handleBackToSearch = useCallback(() => {
    quickPurchaseBackToSearchTracker();

    if (isModal && onClose) {
      onClose();
      return;
    }

    goToSearch();
  }, [goToSearch, isModal, onClose]);

  useEffect(() => {
    if (!userIsLoggedInWithAnyLoyaltyProgram || quickPurchaseLoyaltyExitRef.current) {
      return;
    }

    quickPurchaseLoyaltyExitRef.current = true;
    handleBackToSearch();
  }, [handleBackToSearch, userIsLoggedInWithAnyLoyaltyProgram]);

  const handleToggleCarbonOffset = useCallback(() => {
    actions.onToggleWantsCarbonOffset(token, !wantsCarbonOffset);
  }, [actions, token, wantsCarbonOffset]);

  const enablesCarbon = Boolean(carbonOffset && carbonOffset.available);

  if (orchestrationError) {
    return (
      <div className="p-8 text-center">
        <Text size="L" color="error">
          {orchestrationError}
        </Text>
      </div>
    );
  }

  // Keep the payment section mounted while an inline passenger edit re-locks tickets.
  // Its pending payment intent must survive until passenger and purchaser saves complete.
  const formDefaultsReady = buyerFormDefaultExpanded !== null;
  const isReady =
    !isOrchestrating && formDefaultsReady && (!purchase.isLockingTickets || isEditingPassengers);
  const showNotWsSandboxWarning = features.NOT_WS_SANDBOX && !notWsSandboxWarningDismissed;

  return (
    <div>
      {showNotWsSandboxWarning && (
        <NotWsSandboxWarning onDismiss={() => setNotWsSandboxWarningDismissed(true)} />
      )}
      <div className="lg:bg-white/80 lg:backdrop-blur-lg rounded-t-3xl relative z-[1] py-6 lg:px-5 -mt-[90px] lg:-mt-10">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            {!isModal && (
              <button
                type="button"
                onClick={handleBackToSearch}
                className="flex items-center gap-2 bg-transparent border-none cursor-pointer p-0 block lg:hidden"
              >
                <span
                  className="inline-flex items-center justify-center w-8 h-8 shadow-md
                rounded-full bg-[var(--gray-100)] text-base text-[var(--accent)]"
                >
                  <Icon type="ChevronLeft" size="S" color="grayMedium" />
                </span>
              </button>
            )}
            <img src={quickPurchaseIconSrc} alt="" width={24} height={24} />
            <Text weight="bold" size="XL" className="text-3xl">
              {t('title.quick_purchase')}
            </Text>
          </div>
          {isModal && isReady && <QuickPurchaseTimerBadge remainingTime={timerRemainingTime} />}
          {!isModal && (
            <button
              type="button"
              onClick={handleBackToSearch}
              className="items-center gap-3 bg-transparent border-none cursor-pointer p-0 hidden lg:flex"
            >
              <Text size="S" color="grayMedium" weight="semibold">
                {t('quick_checkout.back_to_schedules')}
              </Text>
              <span
                className="inline-flex items-center justify-center w-8 h-8 shadow-md
                rounded-full bg-[var(--gray-100)] text-base text-[var(--accent)]"
              >
                <Icon type="ChevronLeft" size="S" color="grayMedium" />
              </span>
            </button>
          )}
        </div>

        {!isReady && !isEditingSeats && <LoadingScreen hasHeader={false} />}

        {seatEditInPlaceEnabled && isEditingSeats && (
          <Spacing vertical size="M">
            <button
              type="button"
              onClick={handleSeatsEditCancel}
              className="flex items-center gap-2 bg-transparent border-none cursor-pointer p-0 self-start"
            >
              <span
                className="inline-flex items-center justify-center w-8 h-8 shadow-md
                rounded-full bg-[var(--gray-100)] text-base text-[var(--accent)]"
              >
                <Icon type="ChevronLeft" size="S" color="grayMedium" />
              </span>
              <Text size="S" color="grayMedium" weight="semibold">
                {t('quick_checkout.back_to_summary')}
              </Text>
            </button>
            <SeatSelectorFactory
              way="departure"
              isPurchaseView
              resultsLayout
              embedded
              isRoundTrip={false}
              onFinishSeatSelection={handleSeatsConfirmed}
              onCloseModal={handleSeatsEditCancel}
              frequentPassengersCount={0}
              seatEditMode="quickCheckout"
              requiredSeatsCount={passengers.length}
            />
          </Spacing>
        )}

        {isReady && !isEditingSeats && (
          <Spacing vertical size="L">
            <BookingReview
              alwaysShow
              showTitle={false}
              departureDate={departs.departure}
              arrivalDate={departs.arrival}
              duration={departs.duration}
              departureTerminal={departs.origin?.name}
              departureCity={departs.origin?.cityName}
              destinationTerminal={departs.destination?.name}
              destinationCity={departs.destination?.cityName}
              returnDate={returns.departure}
              passengers={passengers}
              isDepartureOpen={wayIsOpenTicket(departs)}
              isReturnOpen={wayIsOpenTicket(returns)}
              showTripDetailsButton={false}
              showPassengerCategories={false}
            />

            <QuickCheckoutTravelers
              passengers={passengers}
              departureSeats={departs.selectedSeats || []}
              returnSeats={returns.selectedSeats || []}
              onEditSeats={handleSeatsEdit}
              onEditPassengers={handlePassengersEdit}
              isEditingPassengers={isEditingPassengers}
            />

            {isEditingPassengers && (
              <QuickCheckoutPassengersForm
                initialValues={passengersInitialValues}
                onSubmit={handlePassengersSubmit}
                onPassengersSaved={handlePassengersSaved}
                onPassengersSubmitFail={handlePassengersSubmitFail}
                buyerManuallyEdited={buyerManuallyEdited}
              />
            )}

            <InsuranceSection
              enablesInsurance={enablesInsurance}
              wantsInsurance={selectedInsurance}
              hasIneligibleDoc={insuranceDisabled}
              onToggle={handleInsuranceToggle}
              isLoading={updatingInsurance}
              insuranceData={{
                message: insuranceMessage,
                price: insuranceAmount,
                singlePrice: insuranceAmountIsUnitPrice && !showMessageNotInsuranceOpenTicket,
                plusTaxes: insuranceAmountIsUnitPrice && !features.INSURANCE_ON_CHECKOUT_ONLY,
                fullPrice: insuranceAmountWithTaxes,
              }}
              t={t}
            />

            {enablesCarbon && (
              <AncillariesSwitch
                isLoading={updatingCarbonOffset}
                id="carbon-footprint"
                title={carbonOffset?.title}
                icon={carbonOffset?.imageUrl}
                message={
                  <Trans components={{ strong: <strong /> }}>{carbonOffset?.description}</Trans>
                }
                linkText={t('purchase:link.see_more')}
                href={carbonOffset?.urls}
                onChange={handleToggleCarbonOffset}
                checked={wantsCarbonOffset}
                price={carbonOffset?.amount}
              />
            )}

            <PaymentPanel
              purchase={purchase}
              env={env}
              features={features}
              purchaser={purchaser}
              purchaserFormValues={purchaserFormValues}
              cardValues={cardValues}
              actions={actions}
              cardErrorOccured={cardErrorOccured}
              paymentEngine={paymentEngine}
              selectedPaymentMethod={selectedPaymentMethod}
              fetchingPayment={fetchingPayment}
              isUpdating={isUpdating}
              variant="quickPurchase"
              collapsiblePurchaserForm
              purchaserFormDefaultExpanded={buyerFormDefaultExpanded}
              isOrchestrating={isOrchestrating}
              orchestrationError={orchestrationError}
              purchaserSubmitted={purchaserSubmitted}
              onPurchaserSubmitted={handlePurchaserSubmitted}
              onBeforePaymentAsync={isEditingPassengers ? handleBeforePaymentAsync : null}
            />
          </Spacing>
        )}
      </div>
    </div>
  );
};

QuickCheckout.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  goToSearch: PropTypes.func.isRequired,
  orchestrationParams: PropTypes.shape({
    passengersParam: PropTypes.string,
    selectedSeatNumbers: PropTypes.arrayOf(PropTypes.string),
  }),
  isModal: PropTypes.bool,
  onClose: PropTypes.func,
  timerRemainingTime: PropTypes.number,
  onOrchestrationChange: PropTypes.func,
};

QuickCheckout.defaultProps = {
  orchestrationParams: null,
  isModal: false,
  onClose: null,
  timerRemainingTime: 0,
  onOrchestrationChange: null,
};

export default QuickCheckout;
